import { useCallback, useRef, useState } from "react";

/**
 * Encapsulates the import-review dialog's open state and keyboard/focus
 * accessibility plumbing for MyLibrary. Exposes the same shape the page
 * previously had inline so the extraction is purely structural.
 */
export const useImportReviewKeyboard = () => {
  const [importOpen, setImportOpen] = useState(false);
  const [importAnnouncement, setImportAnnouncement] = useState("");
  const [focusedImportReviewRowValidity, setFocusedImportReviewRowValidity] = useState<
    "valid" | "invalid" | null
  >(null);
  const focusedImportReviewRowValidityRef = useRef<"valid" | "invalid" | null>(null);
  const importRestoredFocusTargetRef = useRef<"primary" | "fallback" | null>(null);

  const handleImportOpenChange = useCallback((open: boolean) => {
    setImportOpen(open);
    if (!open) {
      setImportAnnouncement("");
      setFocusedImportReviewRowValidity(null);
      focusedImportReviewRowValidityRef.current = null;
      importRestoredFocusTargetRef.current = null;
    }
  }, []);

  const handleImportEscapeKeyDown = useCallback((event: KeyboardEvent | Event) => {
    if (
      focusedImportReviewRowValidityRef.current === "valid" ||
      focusedImportReviewRowValidityRef.current === "invalid"
    ) {
      event.preventDefault();
      return;
    }

    if (importRestoredFocusTargetRef.current === "fallback") {
      event.preventDefault();

      const dialog = document.querySelector<HTMLElement>('[role="dialog"]');
      const safeControl = dialog?.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );

      safeControl?.focus();

      if (safeControl) {
        importRestoredFocusTargetRef.current = "fallback";
      }
    }
  }, []);

  const handleImportReviewRowFocusChange = useCallback(
    (validity: "valid" | "invalid" | null) => {
      focusedImportReviewRowValidityRef.current = validity;
      setFocusedImportReviewRowValidity(validity);
    },
    [],
  );

  const handleImportFocusRestored = useCallback((target: "primary" | "fallback") => {
    importRestoredFocusTargetRef.current = target;
  }, []);

  return {
    importOpen,
    setImportOpen,
    importAnnouncement,
    setImportAnnouncement,
    focusedImportReviewRowValidity,
    handleImportOpenChange,
    handleImportEscapeKeyDown,
    handleImportReviewRowFocusChange,
    handleImportFocusRestored,
  };
};
